// @flow

import React from 'react';
import type { Node } from 'react';

import { connect } from 'react-redux';

import { Query } from 'react-apollo';

import Logging from '@ta/common.logging';

import { FeaturedDestinationsLoadingSkeleton } from '@ta/social.skeletons';

import FeaturedDestinationsQuery, {
  type FeaturedDestinationsQuery_locations as LocationResult,
  type FeaturedDestinationsQuery_sponsoredHome_profile as SponsorResult,
} from './FeaturedDestinationsQuery.gql';

type FeaturedDestination = {|
  destination: LocationResult,
  sponsor?: SponsorResult,
|};

type ExternalProps = {|
  locationId?: number,
  children: (result: FeaturedDestination[]) => Node,
  onError?: (error: any) => mixed,
|};

type Props = {|
  ...ExternalProps,
  geo: number,
  locale: string,
  altId: string,
|};

// Used to randomize the order of featured destinations
export const arrayShuffle = (arr: number[], altId: string) => {
  const newArr = [...arr];
  for (let i = newArr.length; i && --i; ) {
    const temp = newArr[i];
    const randomDecimal = Math.abs(Math.cos(altId.charCodeAt(i % altId.length)));
    const r = Math.floor(randomDecimal * (i + 1));
    newArr[i] = newArr[r];
    newArr[r] = temp;
  }
  return newArr;
};

const topDestinations2019 = [
  186338,
  187147,
  187791,
  189413,
  294226,
  293920,
  187497,
  293974,
  293734,
  295424,
  274707,
  297390,
  60763,
  147309,
  293924,
  298184,
  150812,
  189158,
  293890,
  304555,
  297549,
  294217,
  294314,
  255060,
  293984,
];

/**
 * Provides a list of featured destinations (where Featured destination = sponsored or Travelers Choice winner)
 * */
const FeaturedDestinationsData = ({ geo, locale, onError, children, altId }: Props) => {
  const geoId = parseInt(geo, 10) || 1;
  const shuffledTopDestinations2019 = arrayShuffle(topDestinations2019, altId);

  return (
    <Query
      errorPolicy="all"
      query={FeaturedDestinationsQuery}
      variables={{
        locale,
        geo: geoId,
        locationIds: shuffledTopDestinations2019,
      }}
      onError={onError}
    >
      {({ data, loading, error }) => {
        if (error) {
          Logging.error(error);
        }
        if (loading) {
          return <FeaturedDestinationsLoadingSkeleton />;
        }
        if (data && data.locations && data.travelersChoice) {
          const topDestinations = (data.locations: any);
          const travelersChoiceDestinations = (data.travelersChoice: any);
          const sponsoredDestination = data.sponsoredHome?.destination;
          const sponsorProfile = data.sponsoredHome?.profile;

          const candidateDestinations = topDestinations
            .map(dest => ({ destination: dest }))
            .filter(dest => dest.destination.locationId !== sponsoredDestination?.locationId);
          if (sponsorProfile && sponsoredDestination) {
            candidateDestinations.push({
              sponsor: sponsorProfile,
              destination: sponsoredDestination,
            });
          }

          // Featured destination = Sponsored or Travelers Choice winner
          const travelersChoiceDestinationIds = travelersChoiceDestinations
            .map(tc => tc?.winnerId)
            .filter(tcId => tcId);
          const featuredDestinations = candidateDestinations.filter(
            dest =>
              dest?.sponsor ||
              travelersChoiceDestinationIds.includes(dest?.destination?.locationId),
          );
          return children(featuredDestinations);
        }
        return null;
      }}
    </Query>
  );
};

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  locale: state.i18n.locale,
  geo: state.route.geo,
  locationId: state.page.geoId || null,
  altId: state.auth.altSessId,
}))(FeaturedDestinationsData);
